import { useEffect, useState } from 'react';

import { Modal, useToast } from '../ui/Overlay';
import { Button } from '../ui/Button';
import { Field, Input, Select, Textarea } from '../ui/Field';
import { Badge } from '../ui/Badge';
import { Icon } from '../ui/Icon';
import { useMutation } from '../../hooks/useResource';
import { useAuth } from '../../auth/AuthProvider';
import { api } from '../../lib/api';
import { ROLES, roleMeta } from '../../lib/domain';
import * as fmt from '../../lib/format';

/**
 * Which roles this account may hand out.
 *
 * The same table the server holds. Duplicated here only to decide what to
 * *offer* — the server refuses independently, so a stale copy in the browser
 * can never become an escalation.
 */
export const ASSIGNABLE_ROLES = {
  SUPER_ADMIN: ['SUPER_ADMIN', 'ORG_ADMIN', 'MANAGER', 'USER'],
  ORG_ADMIN: ['MANAGER', 'USER'],
  MANAGER: ['USER'],
  USER: [],
};

export function assignableRoles(actorRole) {
  return ASSIGNABLE_ROLES[actorRole] || [];
}

/**
 * Change someone's role.
 *
 * A role change ends their sessions immediately — the alternative is a person
 * carrying the old permissions in a live token for up to fifteen minutes,
 * which is exactly the window that matters when the change is a demotion.
 */
export function EditRoleModal({ open, onClose, person, onSaved }) {
  const toast = useToast();
  const { user } = useAuth();
  const [role, setRole] = useState(person?.role || 'USER');

  useEffect(() => {
    if (person) setRole(person.role);
  }, [person]);

  const save = useMutation(async () => {
    await api.patch(`/api/users/${person.id}`, { role });
  });

  const options = assignableRoles(user?.role);
  const canChange = options.length > 0 && options.includes(person?.role);

  const submit = async () => {
    try {
      await save.run();
      toast.success(`${person.full_name} is now ${roleMeta(role).label.toLowerCase()}.`);
      onSaved();
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (!person) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Change role — ${person.full_name}`}
      description={person.email}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={save.pending}
            onClick={submit}
            disabled={!canChange || role === person.role}
          >
            Change role
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {!canChange ? (
          <p className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning-soft px-3 py-2 text-[13px] text-warning-ink">
            <Icon name="shield" size="sm" className="mt-0.5" />
            You cannot change the role of someone who holds {roleMeta(person.role).label.toLowerCase()}.
            Ask a platform administrator.
          </p>
        ) : (
          <>
            <Field label="Role" hint={ROLE_HINTS[role]}>
              <Select
                value={role}
                onChange={(event) => setRole(event.target.value)}
                options={options.map((value) => ({ value, label: ROLES[value].label }))}
              />
            </Field>

            {role !== person.role && (
              <p className="flex items-start gap-2 rounded-lg border border-line bg-surface-2 px-3 py-2 text-[12.5px] text-ink-2">
                <Icon name="info" size="sm" className="mt-0.5 shrink-0" />
                <span>
                  {person.full_name} will be signed out of every device straight away and will sign
                  back in with the new permissions.
                  {role === 'MANAGER' &&
                    ' A manager sees nothing until they are assigned to a fleet.'}
                  {person.role === 'MANAGER' &&
                    role === 'USER' &&
                    ' Their fleet assignments are kept — remove them separately if they should lose those too.'}
                </span>
              </p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

const ROLE_HINTS = {
  SUPER_ADMIN: 'Full access to every organisation on the platform.',
  ORG_ADMIN: 'Runs this organisation: fleets, people, devices and rules.',
  MANAGER: 'Day-to-day operations for the fleets they are assigned to.',
  USER: 'Read-only, and only for the vehicles they are granted.',
};

/**
 * Remove someone.
 *
 * Deactivation, not deletion. Their sessions end and their grants are removed,
 * but the record stays so the audit log can still say who did what. The dialog
 * says exactly that, because "remove" reads as "delete" and people deserve to
 * know which one they are doing.
 */
export function RemovePersonModal({ open, onClose, person, onRemoved }) {
  const toast = useToast();
  const [confirmation, setConfirmation] = useState('');

  useEffect(() => {
    if (open) setConfirmation('');
  }, [open]);

  const remove = useMutation(async () => {
    await api.delete(`/api/users/${person.id}`);
  });

  const submit = async () => {
    try {
      const result = await remove.run();
      toast.success(result?.message || `${person.full_name} removed.`);
      onRemoved();
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (!person) return null;

  const expected = person.full_name.trim();
  const matches = confirmation.trim().toLowerCase() === expected.toLowerCase();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Remove ${person.full_name}?`}
      description={person.email}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="danger" loading={remove.pending} onClick={submit} disabled={!matches}>
            Remove person
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <ul className="space-y-1.5 rounded-lg border border-line bg-surface-2 px-3 py-2.5 text-[13px] text-ink-2">
          <li className="flex gap-2">
            <Icon name="check" size="xs" className="mt-1 shrink-0 text-critical" />
            They are signed out immediately and cannot sign in again.
          </li>
          <li className="flex gap-2">
            <Icon name="check" size="xs" className="mt-1 shrink-0 text-critical" />
            Their vehicle grants and fleet assignments are removed.
          </li>
          <li className="flex gap-2">
            <Icon name="check" size="xs" className="mt-1 shrink-0 text-good" />
            Their history stays. The audit log can still say what they did.
          </li>
        </ul>

        <p className="text-[12.5px] text-ink-3">
          An administrator can restore the account later, but not the access — that is a fresh
          decision.
        </p>

        <Field label={`Type “${expected}” to confirm`} required>
          <Input
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            placeholder={expected}
            autoComplete="off"
          />
        </Field>
      </div>
    </Modal>
  );
}

/** Approve or decline one access request, with a note. */
export function DecideRequestModal({ open, onClose, request, onDecided }) {
  const toast = useToast();
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) setNote('');
  }, [open]);

  const decide = useMutation(async (approve) => {
    await api.post(`/api/access-requests/${request.id}/decide`, { approve, note: note || undefined });
  });

  const run = async (approve) => {
    try {
      await decide.run(approve);
      toast.success(approve ? 'Access granted.' : 'Request declined.');
      onDecided();
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (!request) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Access request"
      description={`${request.requester_name} · ${request.requester_email}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="secondary" loading={decide.pending} onClick={() => run(false)}>
            Decline
          </Button>
          <Button
            variant="primary"
            loading={decide.pending}
            onClick={() => run(true)}
            disabled={!request.canDecide}
          >
            Approve
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <dl className="divide-y divide-line rounded-lg border border-line px-3">
          <div className="flex items-baseline justify-between gap-3 py-2">
            <dt className="text-[12.5px] text-ink-3">Asking for</dt>
            <dd className="text-[13.5px] font-medium text-ink">{requestTarget(request)}</dd>
          </div>
          <div className="flex items-baseline justify-between gap-3 py-2">
            <dt className="text-[12.5px] text-ink-3">Current role</dt>
            <dd>
              <Badge tone={roleMeta(request.requester_role).tone} size="sm" icon={null}>
                {roleMeta(request.requester_role).label}
              </Badge>
            </dd>
          </div>
          <div className="flex items-baseline justify-between gap-3 py-2">
            <dt className="text-[12.5px] text-ink-3">Asked</dt>
            <dd className="text-[13px] text-ink-2">{fmt.relativeTime(request.created_at)}</dd>
          </div>
        </dl>

        {request.reason && (
          <blockquote className="rounded-lg border-l-2 border-brand bg-surface-2 px-3 py-2 text-[13px] text-ink-2">
            {request.reason}
          </blockquote>
        )}

        {!request.canDecide && (
          <p className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning-soft px-3 py-2 text-[13px] text-warning-ink">
            <Icon name="shield" size="sm" className="mt-0.5 shrink-0" />
            {request.cannotDecideReason || 'You cannot approve this one.'} You can still decline it.
          </p>
        )}

        <Field label="Note" hint="The requester sees this. Say why, especially when declining.">
          <Textarea value={note} onChange={(event) => setNote(event.target.value)} rows={2} />
        </Field>
      </div>
    </Modal>
  );
}

export function requestTarget(request) {
  if (request.kind === 'ROLE') return roleMeta(request.requested_role).label;
  if (request.kind === 'VEHICLE') return request.reg_number || 'a vehicle';
  if (request.kind === 'FLEET') return request.fleet_name || 'a fleet';
  return request.kind;
}
